<div class="container mx-auto space-y-4 select-none print:py-0 print:px-0 print:space-y-4">
  <!-- Loading state -->
  @if (loading()) {
    <div class="flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <div
        class="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin"
      ></div>
      <p class="text-slate-500 font-medium animate-pulse">Calculating PRISMA Flow Data...</p>
    </div>
  } @else if (error()) {
    <div
      class="p-6 bg-red-50/80 border border-red-200 rounded-2xl text-center max-w-lg mx-auto shadow-sm"
    >
      <svg
        class="w-12 h-12 mx-auto text-red-500 mb-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h3 class="text-red-800 font-bold text-lg mb-1">Failed to Load Dashboard</h3>
      <p class="text-red-600/90 text-sm mb-4">{{ error() }}</p>
      <button
        (click)="loadData()"
        class="btn btn-secondary px-5 py-2 text-sm font-semibold rounded-lg shadow-sm"
      >
        <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Try Again
      </button>
    </div>
  } @else {
    <!-- Celebration / Congrats Header (Hidden on print) -->
    <div
      class="relative overflow-hidden p-8 rounded-3xl border border-slate-200/60 bg-white/70 backdrop-blur-md shadow-sm flex flex-col md:flex-row items-center gap-6 print:hidden"
    >
      <!-- Confetti decor circle -->
      <div
        class="absolute -right-16 -top-16 w-48 h-48 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -left-16 -bottom-16 w-48 h-48 bg-gradient-to-tr from-secondary/15 to-violet-400/10 rounded-full blur-3xl"
      ></div>

      <!-- Medal Icon -->
      <div
        class="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-600 shadow-md flex items-center justify-center text-white"
      >
        <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
      </div>

      <!-- Texts -->
      <div class="flex-grow text-center md:text-left space-y-1">
        <span
          class="text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200/60 rounded-full px-3 py-1 uppercase tracking-wider inline-block"
        >
          Review Complete
        </span>
        <h1 class="text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight">
          Congratulations! Your Systematic Review is Finalized.
        </h1>
        <p class="text-slate-500 text-sm md:text-base max-w-2xl leading-relaxed">
          The SLR project <strong>{{ project()?.title }}</strong> has reached its ultimate state.
          Below is your official, publication-quality PRISMA 2020 flow chart, ready for export or
          embedding into your research paper.
        </p>
      </div>
    </div>

    <!-- Quick Stats Cards (Hidden on print) -->
    <div class="grid grid-cols-2 md:grid-cols-6 gap-4 print:hidden">
      <div
        class="card p-5 bg-white/60 border border-slate-200/60 shadow-sm flex flex-col justify-between"
      >
        <span class="text-xs font-bold text-slate-400 uppercase tracking-wider"
          >Total Imported</span
        >
        <div class="flex items-baseline gap-1 mt-2">
          <span class="text-3xl font-black text-slate-700 tracking-tight">{{
            prismaStats()?.totalImported
          }}</span>
          <span class="text-slate-400 text-xs">papers</span>
        </div>
      </div>

      <div
        class="card p-5 bg-white/60 border border-slate-200/60 shadow-sm flex flex-col justify-between"
      >
        <span class="text-xs font-bold text-slate-400 uppercase tracking-wider"
          >Duplicates Removed</span
        >
        <div class="flex items-baseline gap-1 mt-2">
          <span class="text-3xl font-black text-rose-500 tracking-tight">{{
            prismaStats()?.duplicateRecordsRemoved
          }}</span>
          <span class="text-slate-400 text-xs">papers</span>
        </div>
      </div>

      <div
        class="card p-5 bg-white/60 border border-orange-200/60 shadow-sm flex flex-col justify-between"
      >
        <span class="text-xs font-bold text-slate-400 uppercase tracking-wider"
          >Incomplete Removed</span
        >
        <div class="flex items-baseline gap-1 mt-2">
          <span class="text-3xl font-black text-orange-500 tracking-tight">{{
            prismaStats()?.incompleteMetadataRemoved
          }}</span>
          <span class="text-slate-400 text-xs">papers</span>
        </div>
      </div>

      <div
        class="card p-5 bg-white/60 border border-slate-200/60 shadow-sm flex flex-col justify-between"
      >
        <span class="text-xs font-bold text-slate-400 uppercase tracking-wider"
          >Screened Stage 1</span
        >
        <div class="flex items-baseline gap-1 mt-2">
          <span class="text-3xl font-black text-blue-500 tracking-tight">{{
            prismaStats()?.firstStageScreened
          }}</span>
          <span class="text-slate-400 text-xs">papers</span>
        </div>
      </div>

      <div
        class="card p-5 bg-white/60 border border-slate-200/60 shadow-sm flex flex-col justify-between"
      >
        <span class="text-xs font-bold text-slate-400 uppercase tracking-wider"
          >Assessed Stage 2</span
        >
        <div class="flex items-baseline gap-1 mt-2">
          <span class="text-3xl font-black text-amber-500 tracking-tight">{{
            prismaStats()?.assessedForEligibility
          }}</span>
          <span class="text-slate-400 text-xs">papers</span>
        </div>
      </div>

      <div
        class="card p-5 bg-gradient-to-tr from-emerald-500/10 to-teal-500/5 border border-emerald-200/50 shadow-sm flex flex-col justify-between relative overflow-hidden"
      >
        <div class="absolute -right-6 -bottom-6 w-16 h-16 bg-emerald-500/5 rounded-full"></div>
        <span class="text-xs font-bold text-emerald-600 uppercase tracking-wider"
          >Included Studies</span
        >
        <div class="flex items-baseline gap-1 mt-2">
          <span class="text-3xl font-black text-emerald-600 tracking-tight">{{
            prismaStats()?.finalIncluded
          }}</span>
          <span class="text-emerald-500 text-xs font-semibold">studies</span>
        </div>
      </div>
    </div>

    @if (project()?.userRole === 'PI') {
      <app-completed-agreement-quality-overview
        [projectId]="projectId()"
        [visible]="true"
      ></app-completed-agreement-quality-overview>
    }

    <!-- Main Workspace: PRISMA Diagram + Controls -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
      <!-- Diagram Column -->
      <div
        class="lg:col-span-8 card p-6 bg-white border border-slate-200 shadow-sm overflow-x-auto print:p-0 print:border-none print:shadow-none print:overflow-visible"
      >
        <div
          class="flex items-center justify-between border-b border-slate-100 pb-4 mb-6 print:hidden"
        >
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 bg-emerald-500 rounded-full"></span>
            <h2 class="text-lg font-bold text-slate-800">PRISMA 2020 Flow Diagram</h2>
          </div>
          <span
            class="text-xs font-medium text-slate-400 bg-slate-100 border border-slate-200/60 rounded px-2.5 py-1"
          >
            Publication-Ready SVG
          </span>
        </div>

        <!-- Custom Crisp PRISMA SVG Flow Diagram -->
        <div class="w-full flex justify-center print:w-full print:block">
          <svg
            id="prisma-svg"
            viewBox="0 0 950 960"
            class="w-full max-w-[840px] h-auto print:max-w-none print:w-full"
            style="
              background-color: #ffffff;
              font-family:
                'Inter',
                system-ui,
                -apple-system,
                sans-serif;
            "
          >
            <!-- Definitions for Markers and Filters -->
            <defs>
              <!-- Arrow head marker -->
              <marker
                id="arrow"
                viewBox="0 0 10 10"
                refX="6"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#64748b" />
              </marker>

              <!-- Subtle drop shadow for nodes -->
              <filter id="shadow" x="-5%" y="-5%" width="110%" height="110%">
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="4"
                  flood-color="#0f172a"
                  flood-opacity="0.04"
                />
              </filter>
            </defs>

            <!-- Vertical grid dividing lines representing phases -->
            <line
              x1="50"
              y1="250"
              x2="900"
              y2="250"
              stroke="#e2e8f0"
              stroke-width="1"
              stroke-dasharray="4 4"
            />
            <line
              x1="50"
              y1="760"
              x2="900"
              y2="760"
              stroke="#e2e8f0"
              stroke-width="1"
              stroke-dasharray="4 4"
            />

            <!-- Top Gold Header bar -->
            <rect
              x="90"
              y="15"
              width="770"
              height="45"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <text
              x="475"
              y="42"
              text-anchor="middle"
              style="font-weight: 700; font-size: 14px; fill: #1e293b"
            >
              Identification of studies via databases and registers
            </text>

            <!-- Phase Vertical Titles (Left Side) -->
            <!-- Identification (Vertical Label) -->
            <rect
              x="15"
              y="80"
              width="50"
              height="150"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <text
              x="-155"
              y="44"
              transform="rotate(-90)"
              text-anchor="middle"
              style="font-weight: 700; font-size: 12px; fill: #1e293b; letter-spacing: 0.5px"
            >
              Identification
            </text>

            <!-- Screening (Vertical Label - Spans Row 2 and Row 3) -->
            <rect
              x="15"
              y="280"
              width="50"
              height="210"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <text
              x="-385"
              y="44"
              transform="rotate(-90)"
              text-anchor="middle"
              style="font-weight: 700; font-size: 12px; fill: #1e293b; letter-spacing: 0.5px"
            >
              Screening
            </text>

            <!-- Screening (Vertical Label - Row 4) -->
            <rect
              x="15"
              y="540"
              width="50"
              height="190"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <text
              x="-635"
              y="44"
              transform="rotate(-90)"
              text-anchor="middle"
              style="font-weight: 700; font-size: 12px; fill: #1e293b; letter-spacing: 0.5px"
            >
              Screening
            </text>

            <!-- Included (Vertical Label) -->
            <rect
              x="15"
              y="800"
              width="50"
              height="100"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <text
              x="-850"
              y="44"
              transform="rotate(-90)"
              text-anchor="middle"
              style="font-weight: 700; font-size: 12px; fill: #1e293b; letter-spacing: 0.5px"
            >
              Included
            </text>

            <!-- --------------------------------------- -->
            <!-- PHASE 1: IDENTIFICATION (y = 80 to 230) -->
            <!-- --------------------------------------- -->

            <!-- Box 1.1 Left: Databases -->
            <rect
              x="90"
              y="80"
              width="330"
              height="150"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="90" y="80" width="330" height="150">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 14px;
                  font-family: inherit;
                  font-size: 12px;
                  color: #334155;
                  line-height: 1.4;
                "
              >
                <div style="font-weight: 700; color: #1e293b; margin-bottom: 4px; font-size: 13px">
                  Records identified from*:
                </div>
                <div style="margin-left: 10px; font-size: 12px; color: #334155">
                  Databases (n = {{ prismaStats()?.totalImported }})
                </div>
                <div
                  style="
                    margin-left: 20px;
                    font-size: 11px;
                    color: #64748b;
                    max-height: 70px;
                    overflow-y: auto;
                  "
                >
                  @for (db of getSortedDatabases(); track db.name) {
                    <div>• {{ db.name }} (n = {{ db.count }})</div>
                  }
                </div>
                <div style="margin-left: 10px; font-size: 12px; color: #334155; opacity: 0.6">
                  Registers (n = 0)
                </div>
              </div>
            </foreignObject>

            <!-- Box 1.2 Right: Records Removed Before Screening -->
            <rect
              x="530"
              y="80"
              width="330"
              height="150"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="530" y="80" width="330" height="150">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 14px;
                  font-family: inherit;
                  font-size: 12px;
                  color: #334155;
                  line-height: 1.4;
                "
              >
                <div style="font-weight: 700; color: #1e293b; margin-bottom: 6px; font-size: 13px">
                  Records removed before screening:
                </div>
                <div
                  style="
                    margin-left: 10px;
                    font-size: 12px;
                    color: #334155;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                  "
                >
                  <div>
                    Duplicate records <span style="text-decoration: underline">removed</span
                    ><br />(n = {{ prismaStats()?.duplicateRecordsRemoved }})
                  </div>
                  <div style="opacity: 0.6">
                    Records marked as ineligible by automation tools (n = 0)
                  </div>
                  <div>
                    Records removed for other reasons<br />(n =
                    {{ prismaStats()?.incompleteMetadataRemoved }})
                  </div>
                </div>
              </div>
            </foreignObject>

            <!-- ---------------------------------- -->
            <!-- PHASE 2: SCREENING (y = 280 to 360) -->
            <!-- ---------------------------------- -->

            <!-- Box 2.1 Left: Records Screened -->
            <rect
              x="90"
              y="280"
              width="330"
              height="80"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="90" y="280" width="330" height="80">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  height: 80px;
                  font-family: inherit;
                  color: #334155;
                  line-height: 1.3;
                "
              >
                <div style="font-weight: 700; color: #1e293b; font-size: 13px">
                  Records screened
                </div>
                <div style="font-weight: 700; color: #1e293b; margin-top: 4px; font-size: 13px">
                  (n = {{ prismaStats()?.firstStageScreened }})
                </div>
              </div>
            </foreignObject>

            <!-- Box 2.2 Right: Records Excluded -->
            <rect
              x="530"
              y="280"
              width="330"
              height="80"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="530" y="280" width="330" height="80">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  height: 80px;
                  font-family: inherit;
                  color: #334155;
                  line-height: 1.3;
                "
              >
                <div style="font-weight: 700; color: #1e293b; font-size: 13px">
                  Records excluded**
                </div>
                <div style="font-weight: 700; color: #1e293b; margin-top: 4px; font-size: 13px">
                  (n = {{ prismaStats()?.firstStageExcluded }})
                </div>
              </div>
            </foreignObject>

            <!-- ----------------------------------------- -->
            <!-- PHASE 3: SCREENING CONTINUED (y = 410 to 730) -->
            <!-- ----------------------------------------- -->

            <!-- Box 3.1 Left: Reports Sought for Retrieval -->
            <rect
              x="90"
              y="410"
              width="330"
              height="80"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="90" y="410" width="330" height="80">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  height: 80px;
                  font-family: inherit;
                  color: #334155;
                  line-height: 1.3;
                "
              >
                <div style="font-weight: 700; color: #1e293b; font-size: 13px">
                  Reports sought for retrieval
                </div>
                <div style="font-weight: 700; color: #1e293b; margin-top: 4px; font-size: 13px">
                  (n = {{ prismaStats()?.soughtForRetrieval }})
                </div>
              </div>
            </foreignObject>

            <!-- Box 3.2 Right: Reports Not Retrieved -->
            <rect
              x="530"
              y="410"
              width="330"
              height="80"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="530" y="410" width="330" height="80">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  height: 80px;
                  font-family: inherit;
                  color: #334155;
                  line-height: 1.3;
                "
              >
                <div style="font-weight: 700; color: #1e293b; font-size: 13px">
                  Reports not retrieved
                </div>
                <div style="font-weight: 700; color: #1e293b; margin-top: 4px; font-size: 13px">
                  (n = {{ prismaStats()?.reportsNotRetrieved }})
                </div>
              </div>
            </foreignObject>

            <!-- Box 3.3 Left: Reports Assessed for Eligibility -->
            <rect
              x="90"
              y="540"
              width="330"
              height="80"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="90" y="540" width="330" height="80">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  height: 80px;
                  font-family: inherit;
                  color: #334155;
                  line-height: 1.3;
                "
              >
                <div style="font-weight: 700; color: #1e293b; font-size: 13px">
                  Reports assessed for eligibility
                </div>
                <div style="font-weight: 700; color: #1e293b; margin-top: 4px; font-size: 13px">
                  (n = {{ prismaStats()?.assessedForEligibility }})
                </div>
              </div>
            </foreignObject>

            <!-- Box 3.4 Right: Reports Excluded with Reasons Breakdown -->
            <rect
              x="530"
              y="540"
              width="330"
              height="190"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="530" y="540" width="330" height="190">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 14px;
                  font-family: inherit;
                  font-size: 12px;
                  color: #334155;
                  line-height: 1.4;
                "
              >
                <div style="font-weight: 700; color: #1e293b; font-size: 13px; margin-bottom: 6px">
                  Reports excluded: (n = {{ prismaStats()?.fullTextExcluded }})
                </div>
                <div
                  style="
                    max-height: 120px;
                    overflow-y: auto;
                    font-size: 11.5px;
                    color: #334155;
                    margin-left: 8px;
                  "
                >
                  @if (getSortedReasons().length > 0) {
                    <div style="display: flex; flex-direction: column; gap: 4px">
                      @for (item of getSortedReasons(); track item.reason) {
                        <div>{{ item.reason }} (n = {{ item.count }})</div>
                      }
                    </div>
                  } @else {
                    <div style="font-style: italic; color: #94a3b8">No reports excluded</div>
                  }
                </div>
              </div>
            </foreignObject>

            <!-- ---------------------------------- -->
            <!-- PHASE 4: INCLUDED (y = 800 to 900)  -->
            <!-- ---------------------------------- -->

            <!-- Box 4.1 Bottom: Studies Included in Review -->
            <rect
              x="90"
              y="800"
              width="330"
              height="100"
              rx="8"
              fill="#ffffff"
              stroke="#cbd5e1"
              stroke-width="1.5"
              filter="url(#shadow)"
            />
            <foreignObject x="90" y="800" width="330" height="100">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  height: 100px;
                  font-family: inherit;
                  color: #334155;
                  line-height: 1.3;
                "
              >
                <div style="font-weight: 800; color: #1e293b; font-size: 14px">
                  Reports of included studies
                </div>
                <div style="font-weight: 900; font-size: 20px; color: #1e293b; margin-top: 10px">
                  (n = {{ prismaStats()?.finalIncluded }})
                </div>
              </div>
            </foreignObject>

            <!-- --------------------------------------- -->
            <!-- CONNECTOR PATHS (ARROW LINES)           -->
            <!-- --------------------------------------- -->

            <!-- Arrow Markers are added in `<g>` to group paths cleanly -->
            <g fill="none" stroke="#1e293b" stroke-width="1.2">
              <!-- 1. Central vertical pathways -->
              <!-- Box 1.1 databases to Box 2.1 screened -->
              <path d="M 255 230 L 255 280" marker-end="url(#arrow)" />

              <!-- Box 2.1 screened to Box 3.1 sought -->
              <path d="M 255 360 L 255 410" marker-end="url(#arrow)" />

              <!-- Box 3.1 sought to Box 3.3 assessed -->
              <path d="M 255 490 L 255 540" marker-end="url(#arrow)" />

              <!-- Box 3.3 assessed to Box 4.1 included -->
              <path d="M 255 620 L 255 800" marker-end="url(#arrow)" />

              <!-- 2. Right horizontal pathways for exclusions -->
              <!-- Box 1.1 database to Box 1.2 removed duplicates -->
              <path d="M 420 155 L 520 155" marker-end="url(#arrow)" />

              <!-- Box 2.1 screened to Box 2.2 excluded -->
              <path d="M 420 320 L 520 320" marker-end="url(#arrow)" />

              <!-- Box 3.1 sought to Box 3.2 not retrieved -->
              <path d="M 420 450 L 520 450" marker-end="url(#arrow)" />

              <!-- Box 3.3 assessed to Box 3.4 excluded with reasons -->
              <path d="M 420 580 L 520 580" marker-end="url(#arrow)" />
            </g>
          </svg>
        </div>
      </div>

      <!-- Actions & Details Column (Hidden on print) -->
      <div class="lg:col-span-4 space-y-4 print:hidden">
        <!-- Export Box -->
        <div class="card p-6 bg-white border border-slate-200/60 shadow-sm space-y-4">
          <h3 class="text-sm font-bold text-slate-800 uppercase tracking-wider">
            Export Results & Diagram
          </h3>
          <p class="text-slate-500 text-xs leading-relaxed">
            Download the PRISMA diagram in vector SVG or PNG format, or export the citable
            bibliography of included studies.
          </p>

          <div class="flex flex-col gap-2.5 pt-2">
            <button
              (click)="exportAsSvg()"
              class="flex items-center justify-center gap-2 btn btn-secondary text-sm font-semibold py-2.5 rounded-lg border shadow-sm"
            >
              <i class="bi bi-file-earmark-arrow-down text-base"></i> PRISMA - SVG
            </button>

            <button
              (click)="exportAsPng()"
              class="flex items-center justify-center gap-2 btn btn-secondary text-sm font-semibold py-2.5 rounded-lg border shadow-sm"
            >
              <i class="bi bi-file-earmark-image text-base"></i> PRISMA - PNG
            </button>

            <button
              (click)="exportAsBibtex()"
              class="flex items-center justify-center gap-2 btn btn-secondary text-sm font-semibold py-2.5 rounded-lg border shadow-sm"
            >
              <i class="bi bi-file-earmark-code text-base"></i> Final Paper-List - BibTeX
            </button>

            <button
              (click)="exportAsCsv()"
              class="flex items-center justify-center gap-2 btn btn-secondary text-sm font-semibold py-2.5 rounded-lg border shadow-sm"
            >
              <i class="bi bi-filetype-csv text-base"></i> Extraction Results - CSV
            </button>

            <button
              (click)="exportSearchRecordCsv()"
              class="flex items-center justify-center gap-2 btn btn-secondary text-sm font-semibold py-2.5 rounded-lg border shadow-sm"
            >
              <i class="bi bi-search text-base"></i> Search record - CSV
            </button>
          </div>
        </div>

        <!-- Exclusion Reason Breakdown details -->
        <div class="card p-6 bg-white border border-slate-200/60 shadow-sm space-y-4">
          <div class="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 class="text-sm font-bold text-slate-800 uppercase tracking-wider">
              Exclusion Reasons
            </h3>
            <span
              class="text-xs font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded border border-red-100"
            >
              Stage 2
            </span>
          </div>

          <p class="text-slate-500 text-xs leading-relaxed">
            During Full-Text Eligibility Screening, papers were excluded under the following
            reasons:
          </p>

          <div class="space-y-2 max-h-[220px] overflow-y-auto pr-1">
            @if (getSortedReasons().length > 0) {
              @for (item of getSortedReasons(); track item.reason) {
                <div
                  class="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <span class="text-xs font-semibold text-slate-700">{{ item.reason }}</span>
                  <span class="text-xs font-extrabold text-slate-500">n = {{ item.count }}</span>
                </div>
              }
            } @else {
              <div class="text-center py-6 text-slate-400 text-xs italic">
                No papers were excluded during Stage 2 eligibility.
              </div>
            }
          </div>
        </div>

        <!-- Databases Import details -->
        <div class="card p-6 bg-white border border-slate-200/60 shadow-sm space-y-4">
          <div class="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 class="text-sm font-bold text-slate-800 uppercase tracking-wider">
              Data Provenance
            </h3>
            <span
              class="text-xs font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded border border-blue-100"
            >
              Stage 1
            </span>
          </div>

          <p class="text-slate-500 text-xs leading-relaxed">
            Papers imported into the project corpus by database source:
          </p>

          <div class="space-y-2 max-h-[220px] overflow-y-auto pr-1">
            @if (getSortedDatabases().length > 0) {
              @for (db of getSortedDatabases(); track db.name) {
                <div
                  class="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <span class="text-xs font-semibold text-slate-700">{{ db.name }}</span>
                  <span class="text-xs font-extrabold text-slate-500">n = {{ db.count }}</span>
                </div>
              }
            } @else {
              <div class="text-center py-6 text-slate-400 text-xs italic">
                No database records have been imported.
              </div>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Search reproducibility record -->
    <div class="card p-6 bg-white border border-slate-200/60 shadow-sm">
      <div class="flex items-center justify-between border-b border-slate-100 pb-3 mb-3">
        <h3 class="text-sm font-bold text-slate-800 uppercase tracking-wider">Search Record</h3>
        <span class="text-xs text-slate-500">{{ importJobs().length }} searches</span>
      </div>
      <p class="text-slate-500 text-xs leading-relaxed mb-3">
        Every import that built this corpus, with its query terms, filters, date, and counts, for
        reproducibility.
      </p>
      <app-search-record [jobs]="importJobs()"></app-search-record>
    </div>

    <!-- Bibliography / Study Search Section -->
    <div class="card p-6 bg-white border border-slate-200 shadow-sm space-y-6 print:hidden">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <span class="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
          <h2 class="text-lg font-bold text-slate-800">Project Bibliography & Study Search</h2>
        </div>

        <!-- Search Bar -->
        <div class="relative w-full md:w-96">
          <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </span>
          <input
            type="text"
            [ngModel]="searchQuery()"
            (ngModelChange)="onSearchInput($event)"
            placeholder="Search papers by title, authors, or DOI..."
            class="block w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all"
          />
        </div>
      </div>

      <!-- Results Table -->
      <div class="overflow-x-auto border border-slate-100 rounded-xl">
        <table class="w-full text-left text-sm">
          <thead class="bg-slate-50 text-slate-500 font-semibold border-b border-slate-100">
            <tr>
              <th class="px-4 py-3">Paper Title & Authors</th>
              <th class="px-4 py-3">Status</th>
              <th class="px-4 py-3">Exclusion Reason</th>
              <th class="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-50">
            @if (loadingPapers()) {
              <tr>
                <td colspan="4" class="px-4 py-12 text-center text-slate-400">
                  <div class="flex flex-col items-center gap-2">
                    <div
                      class="w-6 h-6 border-2 border-secondary border-t-transparent rounded-full animate-spin"
                    ></div>
                    <span>Searching papers...</span>
                  </div>
                </td>
              </tr>
            } @else if (papers().length === 0) {
              <tr>
                <td colspan="4" class="px-4 py-12 text-center text-slate-400">
                  <svg
                    class="w-12 h-12 mx-auto text-slate-200 mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0a2 2 0 01-2 2H6a2 2 0 01-2-2m16 0l-4 4m-8-4l4 4m0 0l4-4m-4 4l-4-4"
                    />
                  </svg>
                  <p>No papers found matching your search.</p>
                </td>
              </tr>
            } @else {
              @for (paper of papers(); track paper.id) {
                <tr class="hover:bg-slate-50/50 transition-colors">
                  <td class="px-4 py-4 max-w-md">
                    <div class="font-bold text-slate-800 line-clamp-1" [title]="paper.title">
                      {{ paper.title }}
                    </div>
                    <div class="text-xs text-slate-500 mt-0.5 line-clamp-1" [title]="paper.authors">
                      {{ paper.authors }}
                    </div>
                    @if (paper.doi) {
                      <div
                        class="text-[10px] text-slate-400 mt-1 font-mono uppercase tracking-tighter"
                      >
                        {{ paper.doi }}
                      </div>
                    }
                  </td>
                  <td class="px-4 py-4">
                    <span
                      class="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border"
                      [ngClass]="{
                        'bg-emerald-50 text-emerald-600 border-emerald-100':
                          paper.status === 'Included',
                        'bg-rose-50 text-rose-600 border-rose-100':
                          paper.status.startsWith('Excluded'),
                        'bg-slate-50 text-slate-500 border-slate-100': !paper.status,
                      }"
                    >
                      {{ paper.status }}
                    </span>
                  </td>
                  <td class="px-4 py-4">
                    @if (paper.exclusionReason) {
                      <span class="text-slate-600 font-medium italic"
                        >"{{ paper.exclusionReason }}"</span
                      >
                    } @else {
                      <span class="text-slate-300">-</span>
                    }
                  </td>
                  <td class="px-4 py-4 text-right">
                    @if (paper.hasExtractedData) {
                      <button
                        (click)="viewExtraction(paper)"
                        class="btn btn-secondary py-1.5 px-3 text-[11px] font-bold rounded-lg border shadow-sm inline-flex items-center gap-1.5"
                      >
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                          />
                        </svg>
                        View Extraction
                      </button>
                    } @else {
                      <span class="text-slate-300">&mdash;</span>
                    }
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      @if (papersError()) {
        <div
          class="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600"
        >
          {{ papersError() }}
        </div>
      }

      <!-- Enhanced Pagination -->
      <div
        class="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100"
      >
        <div class="flex items-center gap-6">
          <span class="text-sm text-slate-500 font-medium">
            Showing <strong>{{ papers().length }}</strong> of
            <strong>{{ totalPapers() }}</strong> papers
          </span>

          <div class="flex items-center gap-2">
            <span class="text-[11px] text-slate-400 font-bold uppercase tracking-wider"
              >Per Page:</span
            >
            <select
              [ngModel]="pageSize()"
              (ngModelChange)="onPageSizeChange($event)"
              class="bg-slate-50 border border-slate-200 rounded-lg text-xs px-2 py-1.5 outline-none focus:ring-2 focus:ring-secondary/20 transition-all"
            >
              <option [value]="10">10</option>
              <option [value]="25">25</option>
              <option [value]="50">50</option>
              <option [value]="100">100</option>
            </select>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            type="button"
            (click)="prevPage()"
            [disabled]="page() === 0 || loadingPapers()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            « Prev
          </button>
          <button
            type="button"
            (click)="nextPage()"
            [disabled]="(page() + 1) * pageSize() >= totalPapers() || loadingPapers()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next »
          </button>
        </div>
      </div>

      <!-- Finalized Systematic Review Protocol Section -->
      <section
        class="card card-padding mt-6 bg-white/70 border border-slate-200/60 shadow-sm print:break-inside-avoid"
      >
        <div class="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
          <div>
            <h2 class="text-lg font-bold text-slate-800">Finalized Systematic Review Protocol</h2>
            <p class="text-xs text-slate-500 mt-0.5">
              The predefined study configuration, search parameters, and screening rules followed
              for this review.
            </p>
          </div>
          @if (protocol()?.publishedAt) {
            <span
              class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 border border-emerald-200"
            >
              ✓ Published on {{ protocol()?.publishedAt | date: 'mediumDate' }}
            </span>
          }
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div>
              <h3 class="text-sm font-bold text-slate-800 mb-1.5">Research Questions</h3>
              <div
                class="p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm text-slate-600 whitespace-pre-wrap leading-relaxed"
              >
                {{ protocol()?.researchQuestions || 'None defined.' }}
              </div>
            </div>

            <div>
              <h3 class="text-sm font-bold text-slate-800 mb-1.5">Search Strategy & Methodology</h3>
              <div
                class="p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm text-slate-600 whitespace-pre-wrap leading-relaxed"
              >
                {{ protocol()?.searchMethodology || 'None defined.' }}
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <h3 class="text-sm font-bold text-slate-800 mb-1.5">
                Cohen's Kappa Target Agreement
              </h3>
              <div
                class="p-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-mono font-bold text-slate-700 inline-block min-w-[80px] text-center"
              >
                {{ protocol()?.cohenKappaThreshold | number: '1.2-3' }}
              </div>
            </div>

            <!-- Inclusion / Exclusion Criteria lists -->
            <div class="border border-slate-100 rounded-2xl p-4 space-y-4 bg-slate-50/50">
              <div>
                <h4 class="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">
                  Inclusion Criteria
                </h4>
                <ul class="list-disc list-inside text-sm text-slate-600 space-y-1.5">
                  @for (item of inclusionCriteriaList(); track $index) {
                    <li>{{ item }}</li>
                  } @empty {
                    <li class="list-none italic text-slate-400 text-xs">
                      No inclusion criteria defined.
                    </li>
                  }
                </ul>
              </div>

              <div class="border-t border-slate-100 pt-3">
                <h4 class="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">
                  Exclusion Criteria
                </h4>
                <ul class="list-disc list-inside text-sm text-slate-600 space-y-1.5">
                  @for (item of exclusionCriteriaList(); track $index) {
                    <li>{{ item }}</li>
                  } @empty {
                    <li class="list-none italic text-slate-400 text-xs">
                      No exclusion criteria defined.
                    </li>
                  }
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  }
</div>
